import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, inject, provideAppInitializer, signal } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { provideAgrBusinessPartnerAggregationApi } from '@next/core-lib/business-partner-aggregation-api';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { I18nEffects, provideAgrI18nShared } from '@next/core-lib/i18n';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { SiteEffects, provideAgrSite } from '@next/core-lib/site';
import { provideAgrTable } from '@next/core-lib/table';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';

import { setEnvironment } from 'src/environment';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { OverviewEffects } from './app/overview/store/effects/overview.effects';
import { reducer as overviewReducer } from './app/overview/store/reducers/overview.reducer';
import { ExternalLabResultsTokenInterceptor } from './app/shared/services/external-lab-results-token.interceptor';
import { AppEffects } from './app/store/effects';
import { reducer } from './app/store/reducer';
import { environment } from './environments/environment';

const loadedConfig = signal(environment);

const setupRuntimeConfig = (async () => {
  try {
    const response = await window.fetch('/assets/config.json', { cache: 'no-store' });
    if (response.ok) {
      const config = (await response.json()) as Record<string, string>;
      loadedConfig.set({ ...environment, ...config });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (error) {
    console.warn('Failed to load runtime environment variables', error);
  }
})();

const registerAppIcons = (): void => {
  const matIcon = inject(MatIconRegistry);
  const sanitizer = inject(DomSanitizer);

  matIcon.addSvgIconLiteral(
    'home-dashboard',
    sanitizer.bypassSecurityTrustHtml(
      // eslint-disable-next-line max-len
      `<svg xmlns="http://www.w3.org/2000/svg" height="100%" viewBox="0 -960 960 960" width="100%" fit="" preserveAspectRatio="xMidYMid meet" focusable="false"><path d="M407-325q23 23 66.5 21.5T537-336l216-339-335 219q-30 20-32 64t21 67Zm71-474q57 0 119 18.5T716-717l-52 37q-45-30-96.5-44.5T477.978-739q-140.472 0-239.225 100.215Q140-538.569 140-396.017 140-351 152.5-305q12.5 46 35.5 85h579q22-36 35-84t13-94q0-42-12.5-90.5T758-578l39-52q38 56 57 112.5T875-404q2 60-12 113t-41 98q-12 23-25.5 28t-33.5 5H192q-17 0-33.5-8.5T134-193q-26-48-40-97.5T80-396q0-83 31.5-156.5t85.5-128Q251-735 323.68-767T478-799Zm-9 331Z"></path></svg>`,
    ),
  );

  // This icon is in the Google Material Symbols font while we are still using the Google Material Icons (deprecated) font
  // Once we migrate to the Google Material Symbols font, we can remove this icon
  matIcon.addSvgIconLiteral(
    'lab_profile',
    sanitizer.bypassSecurityTrustHtml(
      // eslint-disable-next-line max-len
      `<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="M320-480h320v-80H320v80Zm0-160h320v-80H320v80Zm478 499L636-352q-17-23-42-35.5T540-400H160v-400q0-33 23.5-56.5T240-880h480q33 0 56.5 23.5T800-800v640q0 5-.5 9.5T798-141ZM240-80q-33 0-56.5-23.5T160-160v-160h380q10 0 18.5 4.5T573-303L741-83q-5 2-10.5 2.5T720-80H240Z"/></svg>`,
    ),
  );
};

if (environment.PRODUCTION === 'true') {
  enableProdMode();
}

setupRuntimeConfig
  .then(() =>
    bootstrapApplication(AppComponent, {
      providers: [
        provideRouter(routes, withEnabledBlockingInitialNavigation()),
        provideAgrBrowser(),
        provideAgrCore(),
        provideAgrElasticApi(),
        provideAgrAdvisorRelationshipsSelector(),
        provideAgrBusinessPartnerAggregationApi(),
        provideAgrSettings(),
        provideAgrI18nShared(),
        provideAgrTable(),
        provideAgrSite(),
        provideAgrNavigation(),
        provideAgrWrapperSiteSidenav(),
        provideHttpClient(withInterceptorsFromDi()),
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ExternalLabResultsTokenInterceptor,
          multi: true,
        },
        provideStore(
          {},
          {
            runtimeChecks: {
              strictStateImmutability: true,
              strictActionImmutability: true,
              strictStateSerializability: false,
              strictActionSerializability: false,
            },
          },
        ),
        provideState('app', reducer),
        provideState('overview', overviewReducer),
        provideEffects(
          CoreEffects,
          I18nEffects,
          I18nBrowserEffects,
          NavigationEffects,
          SiteEffects,
          AppEffects,
          OverviewEffects,
        ),
        provideAgrRouterStore(),
        provideAppInitializer(registerAppIcons),
        {
          provide: ENVIRONMENT_MAPPER,
          useFactory: () => setEnvironment(loadedConfig()),
        },
      ],
    }),
  )
  .catch((err) => console.error(err));
